/**
 * Servicio de operaciones de autenticación PinPad Web v2.6.0
 * 
 * Operaciones implementadas:
 * - login: Autenticación con credenciales MIT
 * - getKeysRSA: Obtención de llaves de encriptación RSA
 * 
 * Según guía de integración:
 * - Estos dos métodos son OBLIGATORIOS
 * - Se ejecutan UNA SOLA VEZ por sesión
 * - Deben completarse antes de cualquier operación de pago
 * 
 * @module pinpad-v2/operations/auth-service
 */

import messageChannelService from '../message-channel.service'
import { withRetry } from '../../../utilities/pinpad-v2/retry'
import { validateResponse } from '../../../utilities/pinpad-v2/validator'
import { logInfo, logError, logWarn, setUserContext } from '../../../utilities/pinpad-v2/logger'
import { logFlowStep } from '../../../utilities/pinpad-v2/flow-logger'

class AuthService {
  normalizeGetKeysResponse(response = {}) {
    const normalized = {
      ...response,
      publicKeyRSA:
        response.publicKeyRSA ??
        response.PublicKeyRSA ??
        response.publicKey ??
        response.PublicKey ??
        response.keyRSA ??
        response.KeyRSA,
      privateKeyRSA:
        response.privateKeyRSA ??
        response.PrivateKeyRSA ??
        response.privateKey ??
        response.PrivateKey,
    }

    return normalized
  }

  normalizeLoginCredentials(credentials = {}) {
    const rawEnvironment = credentials.Environment ?? credentials.Ambiente
    const normalizedEnvironment = String(rawEnvironment || '').trim().toUpperCase()

    return {
      User: credentials.User ?? credentials.Usuario,
      Pwd: credentials.Pwd ?? credentials.Pass,
      Environment: normalizedEnvironment === 'PRODUCTION'
        ? 'PROD'
        : normalizedEnvironment === 'DEVELOPMENT'
          ? 'QA'
          : normalizedEnvironment
    }
  }

  normalizeGetKeysParams(params = {}) {
    const rawEnvironment = params.Environment ?? params.Ambiente
    const normalizedEnvironment = String(rawEnvironment || '').trim().toUpperCase()

    return {
      Usuario: params.Usuario ?? params.User,
      Pass: params.Pass ?? params.Pwd,
      IdCompany: params.IdCompany,
      IdBranch: params.IdBranch,
      Country: params.Country || 'MX',
      Ambiente: normalizedEnvironment === 'PRODUCTION'
        ? 'PROD'
        : normalizedEnvironment === 'DEVELOPMENT'
          ? 'QA'
          : normalizedEnvironment,
      name: params.name || 'getKeysRSA'
    }
  }

  /**
   * Realiza login en Centro de Pagos MIT
   * 
   * Mensaje según guía oficial:
   * {
   *   "type": "login",
   *   "payload": {
   *     "User": "string",
   *     "Pwd": "string",
   *     "Environment": "QA | PROD"
   *   }
   * }
   * 
   * @param {object} credentials
   * @param {string} credentials.User - Usuario MIT
   * @param {string} credentials.Pwd - Contraseña MIT
   * @param {string} credentials.Environment - 'QA' o 'PROD'
   * @returns {Promise<object>}
   */
  async login(credentials) {
    const operation = 'LOGIN'
    const loginStart = Date.now()
    const normalized = this.normalizeLoginCredentials(credentials)

    logFlowStep({
      phase: 'AUTH',
      step: 'auth.service login started',
      stepNumber: 10,
      status: 'START',
      data: {
        credentials,
        normalized,
      },
    })
    
    logInfo('Starting login operation', { 
      user: normalized.User,
      environment: normalized.Environment 
    })

    // Validar credenciales requeridas
    const missing = []
    if (!normalized.User) missing.push('User/Usuario')
    if (!normalized.Pwd) missing.push('Pwd/Pass')
    if (!normalized.Environment) missing.push('Environment/Ambiente')
    
    if (missing.length > 0) {
      const error = new Error(`Missing required credentials: ${missing.join(', ')}`)
      error.code = 'INVALID_CREDENTIALS'
      logError('Login validation failed', error)
      throw error
    }

    // Validar ambiente
    if (!['QA', 'PROD'].includes(normalized.Environment)) {
      const error = new Error(`Invalid environment: ${normalized.Environment}. Must be 'QA' or 'PROD'`)
      error.code = 'INVALID_ENVIRONMENT'
      logError('Login validation failed', error)
      throw error
    }

    const loginFn = async () => {
      logFlowStep({
        phase: 'AUTH',
        step: 'Sending login message to iframe',
        stepNumber: 11,
        status: 'SENT',
        data: {
          Usuario: normalized.User,
          Pass: normalized.Pwd,
          Ambiente: normalized.Environment,
          name: 'login',
        },
      })

      // Enviar mensaje de login al iframe
      const response = await messageChannelService.sendMessage('login', {
        Usuario: normalized.User,
        Pass: normalized.Pwd,
        Ambiente: normalized.Environment,
        name: "login"
      }, 30000) // Timeout 30s para login

      logFlowStep({
        phase: 'AUTH',
        step: 'Login response received in auth.service',
        stepNumber: 12,
        status: 'RECEIVED',
        duration: Date.now() - loginStart,
        data: {
          response,
        },
      })

      // Validar respuesta
      validateResponse(response, operation)

      // Establecer contexto de usuario para Sentry/logging
      if (response.RESPUESTA === 'ok') {
        setUserContext({
          username: normalized.User,
          environment: normalized.Environment
        })
        
        logInfo('User context set for session', {
          user: normalized.User,
          environment: normalized.Environment
        })
      }

      return response
    }

    try {
      const result = await withRetry(loginFn, operation)

      logFlowStep({
        phase: 'AUTH',
        step: 'Login succeeded in auth.service',
        stepNumber: 13,
        status: 'SUCCESS',
        duration: Date.now() - loginStart,
        data: {
          result,
        },
      })
      
      logInfo('Login successful', { 
        user: normalized.User,
        environment: normalized.Environment,
        responseKeys: Object.keys(result)
      })
      
      return result
    } catch (error) {
      logFlowStep({
        phase: 'AUTH',
        step: 'Login failed in auth.service',
        stepNumber: 13,
        status: 'ERROR',
        duration: Date.now() - loginStart,
        error: {
          message: error?.message,
          code: error?.code,
          stack: error?.stack,
        },
      })
      logError('Login failed', {
        user: normalized.User,
        environment: normalized.Environment,
        error: error.message,
        code: error.code
      })
      throw error
    }
  }

  /**
   * Obtiene llaves RSA para encriptación de datos sensibles
   * 
   * Mensaje según guía oficial:
   * {
   *   "type": "getKeysRSA",
   *   "payload": {
   *     "User": "string",
   *     "Pwd": "string",
   *     "IdCompany": "string",
   *     "IdBranch": "string",
   *     "Country": "MX",
   *     "Environment": "QA | PROD"
   *   }
   * }
   * 
   * @param {object} params
   * @param {string} params.User - Usuario MIT
   * @param {string} params.Pwd - Contraseña MIT
   * @param {string} params.IdCompany - ID de empresa
   * @param {string} params.IdBranch - ID de sucursal
   * @param {string} [params.Country='MX'] - País (default: MX)
   * @param {string} params.Environment - 'QA' o 'PROD'
   * @returns {Promise<object>} - Response con publicKeyRSA y privateKeyRSA
   */
  async getKeysRSA(params) {
    const operation = 'GET_KEYS'
    const keysStart = Date.now()
    const normalized = this.normalizeGetKeysParams(params)

    logFlowStep({
      phase: 'KEYS',
      step: 'auth.service getKeysRSA started',
      stepNumber: 14,
      status: 'START',
      data: {
        params,
        normalized,
      },
    })
    
    logInfo('Starting getKeysRSA operation', {
      user: normalized.Usuario,
      company: normalized.IdCompany,
      branch: normalized.IdBranch,
      country: normalized.Country,
      environment: normalized.Ambiente
    })

    // Validar parámetros requeridos
    const missing = []
    if (!normalized.Usuario) missing.push('Usuario/User')
    if (!normalized.Pass) missing.push('Pass/Pwd')
    if (!normalized.IdCompany) missing.push('IdCompany')
    if (!normalized.IdBranch) missing.push('IdBranch')
    if (!normalized.Ambiente) missing.push('Ambiente/Environment')
    
    if (missing.length > 0) {
      const error = new Error(`Missing required parameters: ${missing.join(', ')}`)
      error.code = 'INVALID_PARAMETERS'
      logError('getKeysRSA validation failed', error)
      throw error
    }

    // Validar ambiente
    if (!['QA', 'PROD'].includes(normalized.Ambiente)) {
      const error = new Error(`Invalid environment: ${normalized.Ambiente}. Must be 'QA' or 'PROD'`)
      error.code = 'INVALID_ENVIRONMENT'
      logError('getKeysRSA validation failed', error)
      throw error
    }

    const getKeysFn = async () => {
      logFlowStep({
        phase: 'KEYS',
        step: 'Sending getKeysRSA message to iframe',
        stepNumber: 15,
        status: 'SENT',
        data: {
          payload: {
            Ambiente: normalized.Ambiente,
            Usuario: normalized.Usuario,
            Pass: normalized.Pass,
            Country: normalized.Country,
            IdBranch: normalized.IdBranch,
            IdCompany: normalized.IdCompany,
            name: normalized.name,
          },
        },
      })

      // Enviar mensaje de getKeysRSA al iframe
      const response = await messageChannelService.sendMessage('getKeysRSA', {
        Ambiente: normalized.Ambiente,
        Usuario: normalized.Usuario,
        Pass: normalized.Pass,
        Country: normalized.Country,
        IdBranch: normalized.IdBranch,
        IdCompany: normalized.IdCompany,
        name: normalized.name,
      }, 30000) // Timeout 30s para obtener llaves

      logFlowStep({
        phase: 'KEYS',
        step: 'getKeysRSA response received in auth.service',
        stepNumber: 16,
        status: 'RECEIVED',
        duration: Date.now() - keysStart,
        data: {
          response,
        },
      })

      // Validar respuesta
      validateResponse(response, operation)

      const normalizedResponse = this.normalizeGetKeysResponse(response)

      // En entornos MIT reales getKeysRSA puede responder sin llaves explícitas,
      // pero con RESPUESTA=ok y datos de terminal válidos para continuar la sesión.
      if (!normalizedResponse.publicKeyRSA || !normalizedResponse.privateKeyRSA) {
        logWarn('getKeysRSA response without explicit RSA key fields', {
          hasPublicKeyRSA: !!normalizedResponse.publicKeyRSA,
          hasPrivateKeyRSA: !!normalizedResponse.privateKeyRSA,
          respuesta: normalizedResponse?.RESPUESTA,
          responseKeys: Object.keys(normalizedResponse || {}),
        })
      }

      logInfo('RSA keys validated', {
        publicKeyLength: normalizedResponse.publicKeyRSA?.length,
        privateKeyLength: normalizedResponse.privateKeyRSA?.length
      })

      return normalizedResponse
    }

    try {
      const result = await withRetry(getKeysFn, operation)

      logFlowStep({
        phase: 'KEYS',
        step: 'getKeysRSA succeeded in auth.service',
        stepNumber: 17,
        status: 'SUCCESS',
        duration: Date.now() - keysStart,
        data: {
          result,
        },
      })
      
      logInfo('getKeysRSA successful', {
        user: normalized.Usuario,
        company: normalized.IdCompany,
        branch: normalized.IdBranch,
        publicKeyLength: result.publicKeyRSA?.length,
        privateKeyLength: result.privateKeyRSA?.length
      })
      
      return result
    } catch (error) {
      logFlowStep({
        phase: 'KEYS',
        step: 'getKeysRSA failed in auth.service',
        stepNumber: 17,
        status: 'ERROR',
        duration: Date.now() - keysStart,
        error: {
          message: error?.message,
          code: error?.code,
          stack: error?.stack,
        },
      })
      logError('getKeysRSA failed', {
        user: normalized.Usuario,
        company: normalized.IdCompany,
        branch: normalized.IdBranch,
        error: error.message,
        code: error.code
      })
      throw error
    }
  }

  /**
   * Verifica si las credenciales están completas
   * @param {object} credentials
   * @returns {boolean}
   */
  validateCredentials(credentials) {
    const normalized = this.normalizeLoginCredentials(credentials)

    return !!(
      normalized &&
      normalized.User &&
      normalized.Pwd &&
      normalized.Environment
    )
  }

  /**
   * Verifica si los parámetros de getKeysRSA están completos
   * @param {object} params
   * @returns {boolean}
   */
  validateKeysParams(params) {
    const normalized = this.normalizeGetKeysParams(params)

    return !!(
      normalized &&
      normalized.Usuario &&
      normalized.Pass &&
      normalized.IdCompany &&
      normalized.IdBranch &&
      normalized.Ambiente
    )
  }
}

// Singleton
const authService = new AuthService()

export default authService
